import { format, sub } from "date-fns";

export interface DateRangeValue {
  from: Date;
  to: Date;
  previousFrom: Date;
  previousTo: Date;
}

export interface QuickRange {
  key: string;
  label: string;
}

export const DATE_RANGE_KEYS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last7days',
  LAST_30_DAYS: 'last30days',
  LAST_90_DAYS: 'last90days',
  LAST_6_MONTHS: 'last6months',
  LAST_YEAR: 'lastyear',
  THIS_WEEK: 'thisweek',
  THIS_MONTH: 'thismonth',
  THIS_QUARTER: 'thisquarter',
  THIS_YEAR: 'thisyear',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
  LAST_4_WEEKS: 'last4weeks',
  LAST_4_QUARTERS: 'last4quarters',
  LAST_3_YEARS: 'last3years',
  LAST_QUARTERS: 'lastquarter',
  CUSTOM: 'custom',
} as const;

export const DEFAULT_QUICK_RANGES: QuickRange[] = [
  { key: DATE_RANGE_KEYS.YESTERDAY, label: "Yesterday" },
  { key: DATE_RANGE_KEYS.LAST_7_DAYS, label: "Last 7 days" },
  { key: DATE_RANGE_KEYS.LAST_30_DAYS, label: "Last 30 days" },
  { key: DATE_RANGE_KEYS.LAST_90_DAYS, label: "Last 90 days" },
  { key: DATE_RANGE_KEYS.LAST_6_MONTHS, label: "Last 6 months" },
  { key: DATE_RANGE_KEYS.LAST_YEAR, label: "Last year" },
  { key: DATE_RANGE_KEYS.THIS_WEEK, label: "This week" },
  { key: DATE_RANGE_KEYS.THIS_MONTH, label: "This month" },
  { key: DATE_RANGE_KEYS.THIS_YEAR, label: "This year" },
];

export const PERIODIC_QUICK_RANGES: QuickRange[] = [
  { key: DATE_RANGE_KEYS.WEEKLY, label: "Weekly" },
  { key: DATE_RANGE_KEYS.MONTHLY, label: "Monthly" },
  { key: DATE_RANGE_KEYS.QUARTERLY, label: "Quarterly" },
  { key: DATE_RANGE_KEYS.YEARLY, label: "Yearly" },
];

function getDateRangeFromKey(key: string): { from: Date; to: Date } {
  const now = new Date();
  let from = now;
  let to = now;

  switch (key) {
    case DATE_RANGE_KEYS.TODAY:
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case DATE_RANGE_KEYS.YESTERDAY:
      from = sub(now, { days: 1 });
      to = sub(now, { days: 1 });
      break;
    case DATE_RANGE_KEYS.LAST_7_DAYS:
      to = now;
      from = sub(now, { days: 7 });
      break;
    case DATE_RANGE_KEYS.LAST_30_DAYS:
      to = now;
      from = sub(now, { days: 30 });
      break;
    case DATE_RANGE_KEYS.LAST_90_DAYS:
      to = now;
      from = sub(now, { days: 90 });
      break;
    case DATE_RANGE_KEYS.LAST_6_MONTHS:
      to = now;
      from = sub(now, { months: 6 });
      break;
    case DATE_RANGE_KEYS.LAST_YEAR:
      from = new Date(now.getFullYear() - 1, 0, 1); // January 1st of last year
      to = new Date(now.getFullYear() - 1, 11, 31); // December 31st of last year
      break;
    case DATE_RANGE_KEYS.THIS_WEEK:
      const day = now.getDay();
      const diff = now.getDate() - day; // adjust to start from Sunday
      from = new Date(now.getFullYear(), now.getMonth(), diff);
      to = new Date(now.getFullYear(), now.getMonth(), diff + 6);
      break;
    case DATE_RANGE_KEYS.THIS_MONTH:
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = now;
      break;
    case DATE_RANGE_KEYS.THIS_QUARTER:
      const currentQuarter = Math.floor(now.getMonth() / 3);
      from = new Date(now.getFullYear(), currentQuarter * 3, 1);
      to = now;
      break;
    case DATE_RANGE_KEYS.THIS_YEAR:
      from = new Date(now.getFullYear(), 0, 1); // 1/1 of the current year
      to = now;
      break;
    case DATE_RANGE_KEYS.WEEKLY:
    case DATE_RANGE_KEYS.LAST_4_WEEKS:
      to = now;
      // Get the current day of week (0 = Sunday, 6 = Saturday)
      const currentDay = now.getDay();
      // Calculate days to subtract to get to the start of the week (Sunday)
      const daysToSubtract = currentDay;
      // Set from date to the start of the current week (Sunday)
      from = sub(now, { days: daysToSubtract });
      from = sub(from, { weeks: 3 });
      break;
    case DATE_RANGE_KEYS.MONTHLY:
      to = now;
      from = new Date(now.getFullYear(), now.getMonth() - 5, 1); // 1st day of 6 months ago
      break;
    case DATE_RANGE_KEYS.QUARTERLY:
      to = now;
      // Calculate current quarter (0-3)
      const currentQuarterIndex = Math.floor(now.getMonth() / 3);
      // Go back 3 quarters
      const targetQuarter = currentQuarterIndex - 3;
      // Calculate year and start month of the target quarter
      const targetYear = now.getFullYear() + Math.floor(targetQuarter / 4);
      const targetMonth = ((targetQuarter % 4) + 4) % 4 * 3; // Ensure month is not negative
      from = new Date(targetYear, targetMonth, 1);
      break;
    case DATE_RANGE_KEYS.YEARLY:
    case DATE_RANGE_KEYS.LAST_3_YEARS:
      to = now;
      from = new Date(now.getFullYear() - 2, 0, 1); // January 1st of 3 years ago
      break;
  }

  // Ensure to date doesn't exceed today
  const limitDate = new Date();
  const isToDateToday = isSameCalendarDay(to, limitDate);

  // Set time to start of day (00:00:00.000) for from date
  from.setHours(0, 0, 0, 0);
  // Set time to end of day (23:59:59.999) for to date, unless to date is today
  // — in that case cap at current time so we don't request future data.
  if (key !== DATE_RANGE_KEYS.TODAY) {
    if (isToDateToday) {
      to = limitDate;
    } else if (to > limitDate) {
      to = limitDate;
    } else {
      to.setHours(23, 59, 59, 999);
    }
  } else if (to > limitDate) {
    to = limitDate;
  }

  return { from, to };
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function calculatePreviousDateRange(from: Date, to: Date, key: string): { previousFrom: Date; previousTo: Date } {
  if (key === DATE_RANGE_KEYS.TODAY) {
    return {
      previousFrom: sub(from, { days: 1 }),
      previousTo: sub(to, { days: 1 }),
    };
  }
  
  let previousFrom: Date;
  let previousTo: Date;

  // For other ranges, calculate based on the number of days
  const rangeMs = to.getTime() - from.getTime();
  const daysInRange = Math.floor(rangeMs / (24 * 60 * 60 * 1000)) + 1;

  previousTo = new Date(to);

  // First move back one week to ensure we're outside the range
  previousTo = new Date(previousTo.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Keep moving back weeks until we're completely outside the range
  while (previousTo >= from) {
    previousTo = new Date(previousTo.getTime() - 7 * 24 * 60 * 60 * 1000);
  }

  // Calculate previousFrom by subtracting (daysInRange - 1) days from previousTo
  previousFrom = new Date(previousTo.getTime() - (daysInRange - 1) * 24 * 60 * 60 * 1000);

  // Set time to start of day (00:00:00.000) for previousFrom date
  previousFrom.setHours(0, 0, 0, 0);
  // Set time to end of day (23:59:59.999) for previousTo date
  previousTo.setHours(23, 59, 59, 999);

  return { previousFrom, previousTo };
}

export function calculateDateRange(key: string): DateRangeValue {
  const { from, to } = getDateRangeFromKey(key);
  const { previousFrom, previousTo } = calculatePreviousDateRange(from, to, key);
  return {
    from,
    to,
    previousFrom,
    previousTo
  };
}

export function formatDateRange(dateRange: DateRangeValue): {
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
} {
  return {
    from: format(dateRange.from, "yyyy-MM-dd"),
    to: format(dateRange.to, "yyyy-MM-dd"),
    previousFrom: format(dateRange.previousFrom, "yyyy-MM-dd"),
    previousTo: format(dateRange.previousTo, "yyyy-MM-dd")
  };
}

export function calculateCustomDateRange(from: Date, to: Date): DateRangeValue {
  // Set time to start of day (00:00:00.000) for from date
  from.setHours(0, 0, 0, 0);

  const now = new Date();
  if (isSameCalendarDay(to, now)) {
    // to date is today: cap at current time instead of end of day
    to.setTime(now.getTime());
  } else {
    // Set time to end of day (23:59:59.999) for to date
    to.setHours(23, 59, 59, 999);
  }

  const { previousFrom, previousTo } = calculatePreviousDateRange(from, to, 'custom');

  return {
    from,
    to,
    previousFrom,
    previousTo
  };
}

const customRange = calculateCustomDateRange(new Date('2024-01-01'), new Date('2024-01-31'));

/**
 * Recalculates date range for "Today so far" if needed, otherwise returns the cached dates.
 * This ensures that "Today so far" always has the current time as endDate when switching tabs.
 * 
 * @param dateRangeKey - The date range key (e.g., 'today', 'yesterday', etc.)
 * @param cachedDates - The cached dates from filters
 * @returns Object with startDate, endDate, previousStartDate, previousEndDate in ISO format
 */
export function getRecalculatedDatesIfNeeded(
  dateRangeKey: string,
  cachedDates: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
  }
): {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
} {
  // CUSTOM is fixed EXCEPT when its cached `to` lands on today's calendar day —
  // calculateCustomDateRange caps that case at "now", so it must be refreshed
  // on tab switch too, same as the dynamic ranges below.
  if (dateRangeKey === DATE_RANGE_KEYS.CUSTOM && isSameCalendarDay(new Date(cachedDates.endDate), new Date())) {
    const dateRange = calculateCustomDateRange(new Date(cachedDates.startDate), new Date(cachedDates.endDate));
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
    };
  }

  // List of dynamic date ranges that should always be recalculated —
  // every key whose `to` gets capped at "now" in getDateRangeFromKey
  // (i.e. anything but YESTERDAY, LAST_YEAR, CUSTOM which are fixed spans
  // unless CUSTOM's `to` is today, handled above).
  const dynamicDateRanges = [
    DATE_RANGE_KEYS.TODAY,
    DATE_RANGE_KEYS.THIS_WEEK,
    DATE_RANGE_KEYS.THIS_MONTH,
    DATE_RANGE_KEYS.THIS_QUARTER,
    DATE_RANGE_KEYS.THIS_YEAR,
    DATE_RANGE_KEYS.WEEKLY,
    DATE_RANGE_KEYS.MONTHLY,
    DATE_RANGE_KEYS.QUARTERLY,
    DATE_RANGE_KEYS.YEARLY,
    DATE_RANGE_KEYS.LAST_7_DAYS,
    DATE_RANGE_KEYS.LAST_30_DAYS,
    DATE_RANGE_KEYS.LAST_90_DAYS,
    DATE_RANGE_KEYS.LAST_6_MONTHS,
    DATE_RANGE_KEYS.LAST_4_WEEKS,
    DATE_RANGE_KEYS.LAST_4_QUARTERS,
    DATE_RANGE_KEYS.LAST_3_YEARS,
    DATE_RANGE_KEYS.LAST_QUARTERS,
  ];

  // Recalculate dates for dynamic date ranges to ensure endtime is current
  if (dynamicDateRanges.includes(dateRangeKey)) {
    const dateRange = calculateDateRange(dateRangeKey);
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
    };
  }
  
  // For fixed date ranges (Yesterday, Last Week, Custom), use cached values
  return cachedDates;
}

/**
 * Returns the UTC-ISO boundaries of the local calendar day(s) spanning `from`..`to`:
 * `from`'s local day at 00:00:00.000 through `to`'s local day at 23:59:59.999.
 * Used to request full-day buckets from the distribution API regardless of
 * whether `to` is capped at "now" (e.g. "Today so far").
 */
export function getLocalDayUtcBounds(from: Date, to: Date): { startUtc: string; endUtc: string } {
  const startOfDay = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0);
  const endOfDay = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
  return { startUtc: startOfDay.toISOString(), endUtc: endOfDay.toISOString() };
}

/**
 * Human-readable label for an arbitrary ISO startDate/endDate pair, matching
 * the naming used by DEFAULT_QUICK_RANGES where the pair lines up with a
 * common quick range (today/yesterday/last N days), falling back to an
 * explicit "MMM d – MMM d, yyyy" span for custom ranges, or a single
 * "MMM d, yyyy" date when the custom range collapses to one day.
 */
export function getPerformanceHeaderRangeLabel(startDateIso: string, endDateIso: string): string {
  const start = new Date(startDateIso);
  const end = new Date(endDateIso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const todayStart = startOfDay(now);
  const startDay = startOfDay(start);
  const endDay = startOfDay(end);
  const dayCount = Math.round((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000)) + 1;

  if (startDay.getTime() === todayStart.getTime() && endDay.getTime() === todayStart.getTime()) {
    return "Today so far";
  }
  const yesterdayStart = sub(todayStart, { days: 1 });
  if (startDay.getTime() === yesterdayStart.getTime() && endDay.getTime() === yesterdayStart.getTime()) {
    return "Yesterday";
  }
  if (endDay.getTime() === todayStart.getTime() && [7, 30, 90].includes(dayCount - 1)) {
    return `Last ${dayCount - 1} days`;
  }
  if (startDay.getTime() === endDay.getTime()) {
    return format(start, "MMM d, yyyy");
  }
  const startFormat = start.getFullYear() === end.getFullYear() ? "MMM d" : "MMM d, yyyy";
  return `${format(start, startFormat)} – ${format(end, "MMM d, yyyy")}`;
}

/**
 * Derives the Call Status Distribution Trend chart's granularity from the
 * selected range's local calendar day count, mirroring the reporting
 * service's own bucketing rule (hourly <=1 day, daily 1-92 days, monthly >92 days).
 */
export function getDistributionRangeType(from: Date, to: Date): 'hour' | 'day' | 'monthly' {
  const startOfFromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const startOfToDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  const dayCount = Math.round((startOfToDay.getTime() - startOfFromDay.getTime()) / (24 * 60 * 60 * 1000)) + 1;

  if (dayCount <= 1) return 'hour';
  if (dayCount <= 92) return 'day';
  return 'monthly';
}